import React, { lazy, Suspense, useEffect, useState, useCallback } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  getWorkOrderColumns,
  getDataGridStyles,
  getDataGridProps,
  getSlotProps,
  getSearchFieldStyles,
  getSearchInputProps,
  getHeaderStyles,
  type ResponsiveBreakpoint
} from './utils';
import { useWorkOrderFilters, useWorkOrderData } from './hooks';
import useOnlineStatus from '../../../hooks/useOnlineStatus';

const MobileWorkOrderList = lazy(
  () => import('./components/MobileWorkOrderList')
);
const TabletWorkOrderList = lazy(
  () => import('./components/TabletWorkOrderList')
);
const DesktopWorkOrderList = lazy(
  () => import('./components/DesktopWorkOrderList')
);

const LoadingView = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px'
    }}
  >
    <CircularProgress />
  </Box>
);

const WorkOrderListPage: React.FC = () => {
  const { t } = useTranslation('workOrders');
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isOnline = useOnlineStatus();
  const [showOfflineReminder, setShowOfflineReminder] = useState(false);
  const [wasOnline, setWasOnline] = useState(isOnline);

  const breakpoint: ResponsiveBreakpoint = isMobile
    ? 'mobile'
    : isTablet
      ? 'tablet'
      : 'desktop';

  const {
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    searchText,
    inputSearchText,
    filterModel,
    handleSearchChange,
    handleColumnFiltersChange
  } = useWorkOrderFilters();

  const {
    rows,
    rowCount,
    isLoading,
    isOffline: dataIsOffline,
    isFiltered
  } = useWorkOrderData({
    paginationModel,
    sortModel,
    filterModel,
    searchText
  });

  const [mobileItems, setMobileItems] = useState<typeof rows>([]);
  const [mobilePage, setMobilePage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [shouldReloadMobile, setShouldReloadMobile] = useState(false);
  const pageSize = 20;

  const loadMoreItems = useCallback(() => {
    if (!isLoading && hasMore) {
      setPaginationModel({ page: mobilePage, pageSize });
      setMobilePage(prev => prev + 1);
    }
  }, [mobilePage, isLoading, hasMore, setPaginationModel, pageSize]);

  useEffect(() => {
    if (isMobile && rows.length > 0) {
      if (mobilePage === 0 || shouldReloadMobile) {
        setMobileItems(rows);
        setShouldReloadMobile(false);
      } else {
        setMobileItems(prev => [...prev, ...rows]);
      }
      setHasMore(mobileItems.length + rows.length < rowCount);
    }
  }, [
    rows,
    mobilePage,
    isMobile,
    rowCount,
    mobileItems.length,
    shouldReloadMobile
  ]);

  useEffect(() => {
    if (searchText !== undefined) {
      setMobileItems([]);
      setMobilePage(0);
      setHasMore(true);
      setPaginationModel({ page: 0, pageSize });
    }
  }, [searchText, setPaginationModel, pageSize]);

  const columns = getWorkOrderColumns(isMobile, isTablet);
  const rawDataGridStyles = getDataGridStyles(breakpoint, theme);
  const dataGridStyles = {
    ...rawDataGridStyles
  };
  const dataGridProps = getDataGridProps(breakpoint);
  const slotProps = getSlotProps(breakpoint);
  const searchFieldStyles = getSearchFieldStyles();
  const searchInputProps = getSearchInputProps();
  const headerStyles = getHeaderStyles(breakpoint);

  const handleMobileClearSearch = () => {
    handleSearchChange('');
    setMobileItems([]);
    setMobilePage(0);
    setHasMore(true);
    setShouldReloadMobile(true);
    setTimeout(() => {
      setPaginationModel({ page: 0, pageSize });
    }, 0);
  };

  useEffect(() => {
    if (wasOnline && !isOnline) {
      setShowOfflineReminder(true);
    }
    setWasOnline(isOnline);
  }, [isOnline, wasOnline]);

  const handleWorkOrderClick = useCallback(
    (workOrder: { jobNumber?: string }) => {
      if (workOrder?.jobNumber) {
        navigate(`/work/${workOrder.jobNumber}`);
      }
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 2, sm: 3 }
      }}
    >
      {/* Offline mode notification */}
      {dataIsOffline && isFiltered && (
        <Alert
          severity="info"
          sx={{
            mb: 1,
            mt: 1
          }}
        >
          {t('offline.downloadedWorkOrders.showingWithMessage', {
            count: rows?.length || 0
          })}
        </Alert>
      )}

      <Suspense fallback={<LoadingView />}>
        {isMobile ? (
          <MobileWorkOrderList
            searchText={inputSearchText}
            handleSearchChange={handleSearchChange}
            onClearSearch={handleMobileClearSearch}
            rowCount={rowCount}
            isLoading={isLoading}
            onCreate={() => {}}
            mobileItems={mobileItems}
            hasMore={hasMore}
            onLoadMore={loadMoreItems}
            onItemClick={handleWorkOrderClick}
            searchFieldStyles={searchFieldStyles}
            searchInputProps={searchInputProps}
            isOffline={dataIsOffline}
            isFiltered={isFiltered}
          />
        ) : isTablet ? (
          <TabletWorkOrderList
            searchText={inputSearchText}
            handleSearchChange={handleSearchChange}
            rowCount={rowCount}
            isLoading={isLoading}
            onCreate={() => {}}
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={setSortModel}
            filterModel={filterModel}
            handleColumnFiltersChange={handleColumnFiltersChange}
            onRowClick={handleWorkOrderClick}
            searchFieldStyles={searchFieldStyles}
            searchInputProps={searchInputProps}
            headerStyles={headerStyles}
            dataGridStyles={dataGridStyles}
            dataGridProps={dataGridProps}
            slotProps={slotProps}
          />
        ) : (
          <DesktopWorkOrderList
            handleSearchChange={handleSearchChange}
            rowCount={rowCount}
            isLoading={isLoading}
            onCreate={() => {}}
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={setSortModel}
            filterModel={filterModel}
            handleColumnFiltersChange={handleColumnFiltersChange}
            onRowClick={handleWorkOrderClick}
            headerStyles={headerStyles}
            dataGridStyles={dataGridStyles}
            dataGridProps={dataGridProps}
            slotProps={slotProps}
          />
        )}
      </Suspense>

      <Snackbar
        open={showOfflineReminder}
        autoHideDuration={6000}
        onClose={() => setShowOfflineReminder(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setShowOfflineReminder(false)}>
          {t('offline.nowOfflineWarning')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderListPage;
